@if (dataset().type === 'draft') {
  <p>
    <mat-chip>Draft</mat-chip>
    <span> This dataset is not published yet.</span>
  </p>
} @else {
  <!-- https://creativecommons.org/chooser/ -->
  <p xmlns:cc="http://creativecommons.org/ns#">
    This work is licensed under
    <a class="ccby-link" href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a>
  </p>
}

@if (dataset().type === 'public') {
  <mat-card appearance="outlined">
    <mat-card-content>
      {{ authors() }} ({{ publicationYear() }}). <em>{{ dataset().content.title }}</em> [Dataset].
      Norwegian Polar Institute.
      <a
        class="npdc-doi-link"
        href="{{ url() }}"
        target="_blank"
        rel="nofollow noopener noreferrer"
        >{{ url() }}</a
      >
    </mat-card-content>
    <mat-card-actions align="end">
      <a
        mat-button
        target="_blank"
        rel="nofollow noopener noreferrer"
        href="https://citation.doi.org/format?doi={{ dataset().doi }}&style=bibtex&lang=en-GB"
        ><mat-icon>open_in_new</mat-icon>BibTeX</a
      >
      <button mat-button (click)="copyToClipboard()">Copy to clipboard</button>
    </mat-card-actions>
  </mat-card>
}

@if (dataset().content.geojson; as geometry) {
  <h2>Geographical coverage</h2>
  <mat-card appearance="outlined" class="npdc-leaflet">
    <app-leaflet-group [geometry]="geometry"></app-leaflet-group>
  </mat-card>
}

<h2>Summary</h2>
<div class="npdc-markdown" [innerHtml]="dataset().content.summary | markdown"></div>

<!-- labels/projects -->
@if (labels.hasValue()) {
  @let values = labels.value();
  @if (values.length) {
    <h2>Projects</h2>
    <mat-list>
      @for (label of values; track label.id) {
        <mat-list-item>
          <mat-icon matListItemIcon>assignment</mat-icon>
          <span matListItemTitle>{{ label.title }}</span>
          @if (label.url) {
            <span matListItemLine>
              <a href="{{ label.url }}" target="_blank">{{ label.url }}</a>
            </span>
          }

          <div matListItemMeta>
            <button
              mat-icon-button
              matTooltip="Actions"
              [matMenuTriggerFor]="labelActionsMenu"
              [matMenuTriggerData]="{ label: label }"
            >
              <mat-icon>more_vert</mat-icon>
            </button>
          </div>
        </mat-list-item>
      }
    </mat-list>
  }
}

<!-- links -->
@if (dataset().content.links?.length) {
  <h2>Links</h2>
  <mat-list>
    @for (link of dataset().content.links; track link) {
      <mat-list-item>
        <mat-icon matListItemIcon>link</mat-icon>
        <span matListItemTitle>
          <a href="{{ link.href }}" target="_blank">{{ link.title || link.href }}</a>
          ({{ link.rel | titlecase }})</span
        >
      </mat-list-item>
    }
  </mat-list>
}

<!-- keywords -->
@if (dataset().content.keywords?.length) {
  <h2>Keywords</h2>
  <mat-list>
    @for (keyword of dataset().content.keywords; track keyword) {
      <mat-list-item>
        <mat-icon matListItemIcon>label</mat-icon>
        <div matListItemTitle>
          @if (keyword.category) {
            <span>{{ keyword.category }}</span>
          }
          @if (keyword.topic) {
            <span> > {{ keyword.topic }}</span>
          }
          @if (keyword.term) {
            <span> > {{ keyword.term }}</span>
          }
          @if (keyword.variableLevel1) {
            <span> > {{ keyword.variableLevel1 }}</span>
          }
          @if (keyword.variableLevel2) {
            <span> > {{ keyword.variableLevel2 }}</span>
          }
          @if (keyword.variableLevel3) {
            <span> > {{ keyword.variableLevel3 }}</span>
          }
        </div>
      </mat-list-item>
    }
  </mat-list>
}

<!-- timeframes -->
@if (dataset().content.timeframes?.length) {
  <h2>Timeframes</h2>
  <mat-list>
    @for (timeframe of dataset().content.timeframes; track timeframe) {
      <mat-list-item>
        <mat-icon matListItemIcon>timeline</mat-icon>
        <div matListItemTitle><b>Start</b>: {{ timeframe.startDate | date }}</div>
        @if (timeframe.endDate) {
          <div matListItemLine><b>End</b>: {{ timeframe.endDate | date }}</div>
        } @else {
          <div matListItemLine>Ongoing</div>
        }
      </mat-list-item>
    }
  </mat-list>
}

<!-- contributors -->
@if (dataset().content.people?.length) {
  <h2>Contributors</h2>
  <mat-list>
    @for (person of dataset().content.people; track person) {
      <mat-list-item>
        <mat-icon matListItemIcon>person</mat-icon>
        <div matListItemTitle>{{ person.firstName }} {{ person.lastName }}</div>
        @if (person.email) {
          <div matListItemLine>{{ person.email }}</div>
        }
        <div matListItemLine>
          @for (role of person.roles; track role; let last = $last) {
            @switch (role) {
              @case ('author') {
                <span>Author</span>
              }
              @case ('editor') {
                <span>Editor</span>
              }
              @case ('pointOfContact') {
                <span>Point of Contact</span>
              }
              @case ('principalInvestigator') {
                <span>Principal Investigator</span>
              }
              @case ('processor') {
                <span>Processor</span>
              }
              @default {
                <span>{{ role }}</span>
              }
            }
            @if (!last) {
              <span>, </span>
            }
          }
        </div>
        <div matListItemMeta>
          <button
            mat-icon-button
            matTooltip="Actions"
            [matMenuTriggerFor]="contributorActionsMenu"
            [matMenuTriggerData]="{ person: person }"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
        </div>
      </mat-list-item>
    }
  </mat-list>
}

<!-- organisations -->
@if (dataset().content.organisations?.length) {
  <h2>Organisations</h2>
  <mat-list>
    @for (organisation of dataset().content.organisations; track organisation) {
      <mat-list-item>
        <mat-icon matListItemIcon>group</mat-icon>
        <div matListItemTitle>{{ organisation.name }}</div>
        @if (organisation.email) {
          <div matListItemLine>{{ organisation.email }}</div>
        }
        <div matListItemLine>
          @for (role of organisation.roles; track role; let last = $last) {
            @switch (role) {
              @case ('author') {
                <span>Author</span>
              }
              @case ('originator') {
                <span>Originator</span>
              }
              @case ('owner') {
                <span>Owner</span>
              }
              @case ('pointOfContact') {
                <span>Point of Contact</span>
              }
              @case ('principalInvestigator') {
                <span>Principal Investigator</span>
              }
              @case ('processor') {
                <span>Processor</span>
              }
              @case ('resourceProvider') {
                <span>Resource Provider</span>
              }
              @default {
                <span>{{ role }}</span>
              }
            }
            @if (!last) {
              <span>, </span>
            }
          }
        </div>
        <div matListItemMeta>
          <button
            mat-icon-button
            matTooltip="Actions"
            [matMenuTriggerFor]="organisationActionsMenu"
            [matMenuTriggerData]="{ organisation: organisation }"
          >
            <mat-icon>more_vert</mat-icon>
          </button>
        </div>
      </mat-list-item>
    }
  </mat-list>
}

<!-- metadata -->
<h2>Other</h2>
<mat-list>
  <mat-list-item>
    <div matListItemTitle>DOI</div>
    <div matListItemMeta>
      @if (dataset().type === 'draft') {
        <mat-chip style="display: inline-flex !important">Inactive</mat-chip>
      }
    </div>
    <div matListItemLine>
      <a href="https://doi.org/{{ dataset().doi }}" target="_blank"
        >{{ dataset().doi | uppercase }}
      </a>
    </div>
    @if (dataset().type === 'draft') {
      <div matListItemLine>This DOI will be assigned to the dataset when published.</div>
    }
  </mat-list-item>
  @if (dataset().type !== 'draft') {
    <mat-list-item>
      <div matListItemTitle>Published</div>
      <span matListItemLine
        >{{ dataset().published | date }} by
        @if (dataset().publishedBy | resolveUser | async; as user) {
          <a href="mailto:{{ user.email }}">{{ user.email }}</a>
        } @else {
          {{ dataset().publishedBy }}
        }
      </span>
    </mat-list-item>
  }
  <mat-list-item>
    <div matListItemTitle>Created</div>
    <span matListItemLine
      >{{ dataset().created | date }} by
      @if (dataset().createdBy | resolveUser | async; as user) {
        <a href="mailto:{{ user.email }}">{{ user.email }}</a>
      } @else {
        {{ dataset().createdBy }}
      }
    </span>
  </mat-list-item>
  <mat-list-item>
    <div matListItemTitle>Modified</div>
    <span matListItemLine
      >{{ dataset().modified | date }} by
      @if (dataset().modifiedBy | resolveUser | async; as user) {
        <a href="mailto:{{ user.email }}">{{ user.email }}</a>
      } @else {
        {{ dataset().modifiedBy }}
      }
    </span>
  </mat-list-item>
</mat-list>

<mat-menu #contributorActionsMenu>
  <ng-template matMenuContent let-person="person">
    @if (person.url) {
      <a mat-menu-item href="{{ person.url }}" target="_blank">Go to website</a>
    }
    @if (person.orcid) {
      <a mat-menu-item href="https://orcid.org/{{ person.orcid }}" target="_blank"
        >Go to ORCID page</a
      >
    }
    @if (person.email) {
      <a mat-menu-item href="mailto:{{ person.email }}" target="_blank">Send email</a>
    }
    <a
      mat-menu-item
      [routerLink]="['/dataset']"
      [queryParams]="{ q: person.firstName + ' ' + person.lastName }"
      >Show datasets from this person</a
    >
  </ng-template>
</mat-menu>

<mat-menu #organisationActionsMenu>
  <ng-template matMenuContent let-organisation="organisation">
    @if (organisation.url) {
      <a mat-menu-item href="{{ organisation.url }}" target="_blank"> Go to website </a>
    }
    @if (organisation.email) {
      <a mat-menu-item href="mailto:{{ organisation.email }}" target="_blank"> Send email </a>
    }
    <a mat-menu-item [routerLink]="['/dataset']" [queryParams]="{ q: organisation.name }">
      Show datasets from this organisation
    </a>
  </ng-template>
</mat-menu>

<mat-menu #labelActionsMenu>
  <ng-template matMenuContent let-label="label">
    @if (label.url) {
      <a mat-menu-item href="{{ label.url }}" target="_blank">Go to website</a>
    }
    <a mat-menu-item [routerLink]="['/dataset']" [queryParams]="{ labelId: label.id }">
      Show datasets with this label
    </a>
  </ng-template>
</mat-menu>
